import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { effortLabel, type ProgramSimulation } from "@heraion/shared";

import { patternLabel } from "../patternLabel";
import { Eyebrow } from "../primitives/Eyebrow";
import type { TFn } from "../i18nTypes";
import type { UiTheme } from "../theme";
import { Pair, formatSets } from "./simulationShared";
import type { SimulationStyles } from "./simulationStyles";

type Axis = ProgramSimulation["effort"]["untargeted"][number]["axis"];

const axisWord = (t: TFn, axis: Axis): string =>
  axis === "absolute"
    ? t("simulator.axis.absolute", { defaultValue: "a fixed weight" })
    : axis === "percentage"
      ? t("simulator.axis.percentage", { defaultValue: "a % of max" })
      : axis === "history"
        ? t("simulator.axis.history", { defaultValue: "the last session" })
        : t("simulator.axis.none", { defaultValue: "not set" });

// #355: the simulator's distributions. Days a week per movement pattern
// (per muscle sits on its row), the sets by reps and by effort.
export function SimulationFacts({
  theme,
  styles,
  sim,
  locale,
}: {
  theme: UiTheme;
  styles: SimulationStyles;
  sim: ProgramSimulation;
  locale: string;
}) {
  const { t } = useTranslation();
  const sets = (n: number) => t("progress.volumeLandmark.sets", { count: n, n: formatSets(n, locale) });
  const { reps, effort } = sim;
  const repsOther = [
    reps.amrap > 0 ? t("simulator.repsAmrap", { count: reps.amrap, defaultValue: "{{count}} of them AMRAP floors" }) : null,
    reps.timed > 0 ? t("simulator.repsTimed", { count: reps.timed, defaultValue: "{{count}} timed sets" }) : null,
    reps.open > 0
      ? t("simulator.repsOpen", { count: reps.open, defaultValue: "{{count}} open sets, reps written in session" })
      : null,
  ].filter((x): x is string => x !== null);
  return (
    <>
      {sim.frequency.patterns.length > 0 ? (
        <View style={styles.block}>
          <Eyebrow theme={theme}>
            {t("simulator.patternsTitle", { defaultValue: "Days a week per movement" })}
          </Eyebrow>
          {sim.frequency.patterns.map((f) => (
            <Pair
              key={f.key}
              styles={styles}
              label={patternLabel(t, f.key)}
              value={t("simulator.days", {
                count: f.daysPerWeek,
                n: formatSets(f.daysPerWeek, locale),
                defaultValue: "{{n}} days",
              })}
            />
          ))}
        </View>
      ) : null}
      {reps.byReps.length > 0 || repsOther.length > 0 ? (
        <View style={styles.block}>
          <Eyebrow theme={theme}>{t("simulator.repsTitle", { defaultValue: "Sets by reps" })}</Eyebrow>
          {reps.byReps.map((r) => (
            <Pair
              key={r.reps}
              styles={styles}
              label={t("simulator.repsRow", { count: r.reps, defaultValue: "{{count}} reps" })}
              value={sets(r.sets)}
            />
          ))}
          {repsOther.length > 0 ? <Text style={styles.quiet}>{repsOther.join(" · ")}</Text> : null}
        </View>
      ) : null}
      {effort.byRir.length > 0 || effort.untargeted.length > 0 ? (
        <View style={styles.block}>
          <Eyebrow theme={theme}>{t("simulator.effortTitle", { defaultValue: "Sets by effort" })}</Eyebrow>
          {effort.byRir.map((e) => (
            <Pair key={e.rir} styles={styles} label={effortLabel(e.rir, "rir")} value={sets(e.sets)} />
          ))}
          {effort.untargeted.map((u) => (
            <Pair
              key={u.axis}
              styles={styles}
              label={t("simulator.untargeted", {
                axis: axisWord(t, u.axis),
                defaultValue: "No effort target, load: {{axis}}",
              })}
              value={sets(u.sets)}
            />
          ))}
        </View>
      ) : null}
    </>
  );
}
